import PrismicDOM from 'prismic-dom'
import linkResolver from '@/prismic-link-resolver'
import kebabCase from 'lodash/kebabCase'

export function canObserve () {
  return typeof window.IntersectionObserver === 'function'
}

// Imgix thumb resizing
// https://docs.imgix.com/setup/serving-images
export function prismicThumb (src, length, byHeight = false) {
  let url
  const sizes = [320, 480, 640, 800, 1024, 1280, 1440, 1600, 1920, 2560, 2880, 3600, 4096, 5120]
  const dpx = window.devicePixelRatio || 1
  try {
    url = new URL(src)
  } catch (e) {
    console.warn('Invalid URL', src)
    return src
  }
  // w/h already set ?
  const w = url.searchParams.get('w')
  const h = url.searchParams.get('h')
  // update length
  length = parseInt(length * dpx * 0.8) // less density optically ok ?
  // find optimal
  let size = sizes.find(sz => length <= sz) || sizes[sizes.length - 1]
  // resize
  const key = byHeight ? 'h' : 'w'
  url.searchParams.set(key, size)
  // preserve aspect ratio (if defined)
  if (byHeight && w) {
    url.searchParams.set('w', parseInt(size * w / h))
  } else if (h) {
    url.searchParams.set('h', parseInt(size * h / w))
  }
  return url.href
}

export function formatPrjs (prjs) {
  const ratios = { '4:3': 'pb-ar-4x3', '3:2': 'pb-ar-3x2', '4:5': 'pb-ar-4x5', '1:1': 'pb-ar-1x1' }
  return prjs.map(prj => {
    if (!prj || !prj.data) return prj
    const ar = prj.data.index_image__format
    const img = prj.data.index_image || {}
    // the cropped view may no longer exist in the Prismic model (4:3 was replaced by 3:2): fall back to the main image
    prj.data._index_image = (ar && img[ar] && img[ar].url) ? img[ar] : (img.url ? img : null)
    prj.data._ar_class = ratios[ar] || ratios['4:3']
    return prj
  })
}

/**
 * Prismic (imgix) image URL resized to a CSS width, keeping any existing crop and aspect ratio.
 * No crop parameters are added, so the Gallery shows the whole photograph (build brief C3, H2).
 */
export function imgixWidth (src, cssWidth) {
  if (!src) return src
  try {
    const url = new URL(src)
    const sizes = [480, 640, 800, 960, 1280, 1600, 2048, 2560]
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const target = cssWidth * dpr
    const w = sizes.find(sz => target <= sz) || sizes[sizes.length - 1]
    const w0 = parseInt(url.searchParams.get('w'))
    const h0 = parseInt(url.searchParams.get('h'))
    url.searchParams.set('w', w)
    if (w0 && h0) url.searchParams.set('h', Math.round(w * h0 / w0))
    return url.href
  } catch (e) {
    return src
  }
}

/**
 * Media items (images and vimeo clips) across a project's header and body: used by the viewer and the page meta.
 */
export function collectMedia (doc) {
  const media = []
  if (!doc) return media
  const collect = body => body && body.forEach(slice => {
    const type = slice.slice_type || ''
    if (type.includes('image')) {
      (slice.items || []).forEach(itm => itm.image && itm.image.url && media.push({
        type: 'image',
        id: itm.image.url,
        media: itm.image,
        caption: itm.caption
      }))
    }
    if (type === 'vimeo') {
      const data = slice.primary || {}
      if (!data.vimeo_url) return
      media.push({
        type: 'vimeo',
        id: data.vimeo_url,
        media: {
          url: data.vimeo_url,
          clip: [data.clip_start, data.clip_end],
          controls: (data.viewer_settings || '').includes('playable')
        },
        caption: data.caption
      })
    }
  })
  collect(doc.header)
  collect(doc.body)
  return media
}

// touch-first device (no hover): captions stay visible, the Selected Works bar stays up
export function isTouchDevice () {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches
}

export function next (cur, step, total) {
  return cur + step === total ? 0 : cur + step < 0 ? total - 1 : cur + step
}

export function getNav (slices) {
  slices = slices || []
  return slices.filter(slc => slc.slice_type === 'scroll_point').map(pt => {
    const lbl = pt.primary.label
    return typeof lbl === 'string' ? lbl : lbl[0] && lbl[0].text
  })
}

function plainLabel (field) {
  if (!field) return ''
  return typeof field === 'string' ? field : ((field[0] && field[0].text) || '')
}

// drop a page's first scroll_point if its label repeats that page's own main-nav label (e.g. the
// "About" nav item linking straight to a page whose own first scroll-linked subheading is also
// "About"): the word would otherwise appear twice, back to back — most visible in the mobile menu,
// where the sub-list's first entry sits directly under the parent item of the same name
export function dedupeNavLabelScrollPoint (body, navLabel) {
  const heading = (navLabel || '').trim().toLowerCase()
  let dropped = false
  return (body || []).filter(slc => {
    if (!dropped && heading && slc.slice_type === 'scroll_point' && plainLabel(slc.primary.label).trim().toLowerCase() === heading) {
      dropped = true
      return false
    }
    return true
  })
}

export function idy (str) {
  // format ids for scroll points (numbers cant be DOM id)
  return '_' + kebabCase(str)
}

export function superScriptRichText (field) {
  // convert "[2]" footnotes to <sup>2</sup>
  if (!field) return ''
  const text = PrismicDOM.RichText.asHtml(field, linkResolver)
  const replacer = (match) => match.replace('[', '<sup>').replace(']', '</sup>')
  return text.replace(/\[{1}\d+\]{1}/g, replacer)
}

export function isMedia (slice) {
  return slice && ['image_folio', 'vimeo'].includes(slice.slice_type)
}

export function prjYear (project) {
  const date = ((project && project.data && project.data.mural_completion_date) || '').split('-')
  return date && date[0]
}
